import { ChevronRight } from "lucide-react";

import { Button } from "@/design-system/Button";
import { Panel } from "@/design-system/Panel";
import {
  StatusBadge,
  type BadgeShape,
  type BadgeTone,
} from "@/design-system/StatusBadge";
import { ErrorState, Forbidden, Loading } from "@/design-system/states/States";
import type {
  ConformanceProgressResponse,
  ConformanceRunResponse,
  ConformanceSummaryResponse,
} from "@/shared/api";

import { useConformance } from "./useConformance";

const PROOF = [
  ["Сборка", "RELEASE"],
  ["Стратегия", "STRATEGY"],
  ["База", "DATABASE"],
  ["API", "API_READ_MODELS"],
  ["Сайт", "WEB"],
  ["Документы", "DOCUMENTATION"],
] as const;

/** Each check state in words; an unknown state as written. */
const STATE: Readonly<Record<string, string>> = {
  PASS: "в порядке",
  WARNING: "предупреждение",
  DRIFT: "расхождение",
  OWNER_DECISION: "нужно решение владельца",
  ERROR: "ошибка проверки",
  FAILED: "ошибка проверки",
  NOT_APPLICABLE: "не применимо",
  RUNNING: "идёт проверка",
  QUEUED: "в очереди",
};

const PROGRESS: Record<string, string> = {
  WAITING: "Ожидает",
  RUNNING: "Выполняется",
  COMPLETE: "Готово",
  ERROR: "Ошибка",
};

function tone(state: string): BadgeTone {
  if (state === "PASS") return "ready";
  if (state === "RUNNING") return "active";
  if (state === "WARNING" || state === "QUEUED") return "warning";
  if (
    state === "DRIFT" ||
    state === "OWNER_DECISION" ||
    state === "ERROR" ||
    state === "FAILED"
  ) {
    return "danger";
  }
  return "muted";
}

function shape(state: string): BadgeShape {
  if (state === "OWNER_DECISION" || state === "NOT_APPLICABLE") return "dashed";
  if (state === "DRIFT" || state === "PASS") return "outlined";
  return "filled";
}

function statusLabel(state: string): string {
  return STATE[state] ?? state;
}

function latestProgress(
  run: ConformanceRunResponse | null,
): ReadonlyMap<string, ConformanceProgressResponse> {
  const latest = new Map<string, ConformanceProgressResponse>();
  for (const item of run?.progress ?? []) latest.set(item.category, item);
  return latest;
}

function Identity({ summary }: { readonly summary: ConformanceSummaryResponse }) {
  const { release } = summary;
  const rows = [
    ["Стратегия", release.strategy.strategy_version.replace(/^base-/i, "BASE ")],
    ["Candidate", release.strategy.candidate_revision],
    ["Contract", release.strategy.contract.version],
    ["Score", release.score_policy.version],
    ["Capital", release.capital_policy.version],
    ["Allocation", release.allocation_policy.version],
  ] as const;
  return (
    <dl className="grid min-w-0 grid-cols-1 gap-x-5 gap-y-2 sm:grid-cols-2 xl:grid-cols-3">
      {rows.map(([label, value]) => (
        <div key={label} className="min-w-0">
          <dt className="text-label-caps uppercase text-text-muted">{label}</dt>
          <dd className="truncate font-mono text-data-mono text-text-primary" title={value}>
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function ProofChain({ run }: { readonly run: ConformanceRunResponse | null }) {
  const progress = latestProgress(run);
  return (
    <ol
      aria-label="Цепочка доказательств релиза"
      className="grid grid-cols-2 overflow-hidden rounded-row border border-border-subtle sm:grid-cols-3 xl:grid-cols-6"
    >
      {PROOF.map(([label, category], index) => {
        const item = progress.get(category);
        return (
          <li
            key={category}
            data-testid={`proof-${category}`}
            className="relative min-w-0 border-b border-border-subtle px-2.5 py-2 last:border-b-0 sm:border-r xl:border-b-0"
          >
            <span className="block text-label-caps uppercase text-text-secondary">{label}</span>
            <span className="block text-helper-sm text-text-muted">
              {item === undefined ? "Не проверено" : (PROGRESS[item.state] ?? item.state)}
            </span>
            {index < PROOF.length - 1 && (
              <ChevronRight
                aria-hidden="true"
                className="absolute top-1/2 right-0 hidden size-3 -translate-y-1/2 text-border-strong xl:block"
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function Findings({ run }: { readonly run: ConformanceRunResponse }) {
  if (run.findings.length === 0) {
    return <p className="text-helper-sm text-text-muted">Findings не зафиксированы.</p>;
  }
  return (
    <ul className="divide-y divide-border-subtle">
      {run.findings.map((finding) => (
        <li key={finding.fingerprint} className="py-2 first:pt-0 last:pb-0">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge
              testId={`finding-status-${finding.fingerprint}`}
              tone={tone(finding.status)}
              shape={shape(finding.status)}
            >
              {statusLabel(finding.status)}
            </StatusBadge>
            <span className="text-body-main text-text-primary">{finding.title}</span>
            <span className="font-mono text-helper-sm text-text-muted">
              {finding.check_id}
            </span>
          </div>
          <dl className="mt-1 grid grid-cols-1 gap-1 text-helper-sm sm:grid-cols-2">
            <div>
              <dt className="text-text-muted">Ожидалось</dt>
              <dd className="text-text-secondary">{finding.expected}</dd>
            </div>
            <div>
              <dt className="text-text-muted">Обнаружено</dt>
              <dd className="text-text-secondary">{finding.actual}</dd>
            </div>
          </dl>
          <p className="mt-1 text-helper-sm text-text-secondary">
            {finding.suggested_action}
          </p>
        </li>
      ))}
    </ul>
  );
}

function TechnicalDetails({ summary }: { readonly summary: ConformanceSummaryResponse }) {
  const { release, latest } = summary;
  const details = [
    ["Production SHA", release.production_sha],
    ["Canonical SHA", release.canonical_git_sha],
    ["Candidate SHA-256", release.strategy.candidate_sha256],
    ["Contract SHA-256", release.strategy.contract.content_sha256],
    ["Score SHA-256", release.score_policy.content_sha256],
    ["Capital SHA-256", release.capital_policy.content_sha256],
    ["Allocation SHA-256", release.allocation_policy.content_sha256],
    ["Run ID", latest?.run_id ?? "—"],
    ["Catalog", latest?.catalog_version ?? "—"],
    ["Migration", release.db_migration_level],
  ] as const;
  return (
    <details className="border-t border-border-subtle pt-2 text-helper-sm">
      <summary className="cursor-pointer text-text-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring">
        Technical Details
      </summary>
      <dl className="mt-2 grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2">
        {details.map(([label, value]) => (
          <div key={label} className="min-w-0">
            <dt className="text-label-caps uppercase text-text-muted">{label}</dt>
            <dd className="break-all font-mono text-data-mono text-text-secondary">{value}</dd>
          </div>
        ))}
      </dl>
      {latest?.findings.flatMap((finding) => finding.evidence).map((item) => (
        <p key={`${item.name}:${item.value}`} className="mt-1 font-mono text-data-mono">
          {item.name}: {item.value}
        </p>
      ))}
      {latest?.findings.map((finding) => (
        <div key={`source:${finding.fingerprint}`} className="mt-2 border-t border-border-subtle pt-2">
          <p className="font-mono text-data-mono">{finding.check_id}</p>
          <p>Authority: {finding.authority_source}</p>
          <p>Источник наблюдения: {finding.observed_source}</p>
          <p>Expected: {finding.expected}</p>
          <p>Actual: {finding.actual}</p>
        </div>
      ))}
    </details>
  );
}

function Ready({ state }: { readonly state: Extract<ReturnType<typeof useConformance>, { status: "ready" }> }) {
  const { summary, history } = state;
  const latest = summary.latest;
  const active = latest !== null && (latest.state === "QUEUED" || latest.state === "RUNNING");
  return (
    <Panel
      title="Соответствие релиза"
      testId="release-conformance"
      trailing={
        <Button
          variant="primary"
          size="sm"
          disabled={state.starting || active}
          onClick={state.startFull}
        >
          {state.starting ? "Ставлю в очередь…" : "Запустить полную проверку"}
        </Button>
      }
    >
      <div className="flex min-w-0 flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-label-caps uppercase text-text-muted">Последний Full</span>
          <StatusBadge
            testId="conformance-verdict"
            tone={tone(latest?.state ?? "UNKNOWN")}
            shape={shape(latest?.state ?? "UNKNOWN")}
          >
            {latest === null ? "Ещё не запускался" : statusLabel(latest.state)}
          </StatusBadge>
          {latest?.finished_at !== null && latest?.finished_at !== undefined && (
            <span className="text-helper-sm text-text-muted">
              {new Date(latest.finished_at).toLocaleString("ru-RU")}
            </span>
          )}
        </div>
        {state.startFailed && (
          <p role="alert" className="text-helper-sm text-status-negative">
            Полную проверку не удалось поставить в очередь.
          </p>
        )}
        <ProofChain run={latest} />
        <Identity summary={summary} />
        {latest === null ? (
          <p className="text-helper-sm text-text-muted">
            Запустите Full Conformance, чтобы сохранить первый immutable run.
          </p>
        ) : (
          <Findings run={latest} />
        )}
        <TechnicalDetails summary={summary} />
        <div className="border-t border-border-subtle pt-2">
          <h3 className="text-label-caps uppercase text-text-secondary">История запусков</h3>
          {history.runs.length === 0 ? (
            <p className="mt-1 text-helper-sm text-text-muted">История пока пуста.</p>
          ) : (
            <ul className="mt-1 divide-y divide-border-subtle">
              {history.runs.map((run) => (
                <li
                  key={run.run_id}
                  data-testid={`conformance-history-${run.run_id}`}
                  className="flex flex-wrap items-center justify-between gap-2 py-1.5"
                >
                  <span className="font-mono text-data-mono text-text-secondary">
                    {run.run_id}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="text-helper-sm text-text-muted">{run.mode}</span>
                    <StatusBadge tone={tone(run.state)} shape={shape(run.state)}>
                      {statusLabel(run.state)}
                    </StatusBadge>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Panel>
  );
}

export function ReleaseConformance(): React.JSX.Element {
  const state = useConformance();
  if (state.status === "loading") {
    return <Loading title="Читаю соответствие релиза…" />;
  }
  if (state.status === "forbidden") return <Forbidden />;
  if (state.status === "unavailable") {
    return (
      <ErrorState
        title="Проверка соответствия релиза не настроена"
        detail="Release Manifest или каталог проверок не подключён. Это не считается PASS."
      />
    );
  }
  if (state.status === "unreadable") {
    return (
      <ErrorState
        title="Не могу прочитать соответствие релиза"
        detail="Записи прогонов не пришли. Торговое состояние не изменялось."
      />
    );
  }
  return <Ready state={state} />;
}
